import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { ELEMENT_CONFIG } from '../../utils/elementConfig';

const LogForm = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [floors, setFloors] = useState([]);
  const [formData, setFormData] = useState({
    project_id: '',
    floor_id: '',
    log_date: new Date().toISOString().split('T')[0],
    element_category: '',
    element_detail: '',
    
    // PHASE MANHOURS (not checkboxes anymore)
    manhours_forming: '',
    manhours_pouring: '',
    manhours_stripping: '',
    manhours_punch: '',
    
    cubic_yards: '',
    crew_size: '',
    equipment_used: '',
    equipment_hours: '',
    plywood_reuse: null,
    scaffold_tiers: '',
    formwork_notes: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await api.get('/projects');
      setProjects(response.data.filter(p => p.status === 'active'));
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchFloors = async (projectId) => {
    try {
      const response = await api.get(`/project/${projectId}/floors`);
      setFloors(response.data);
    } catch (error) {
      console.error('Error fetching floors:', error);
      setFloors([]);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'project_id') {
      setFormData({
        ...formData,
        project_id: value,
        floor_id: ''
      });
      if (value) {
        fetchFloors(value);
      } else {
        setFloors([]);
      }
    } else if (name === 'element_category') {
      setFormData({
        ...formData,
        element_category: value,
        element_detail: ''
      });
    } else if (name === 'plywood_reuse') {
      setFormData({
        ...formData,
        plywood_reuse: value === 'true' ? true : value === 'false' ? false : null
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const calculateTotals = () => {
    const forming = parseFloat(formData.manhours_forming) || 0;
    const pouring = parseFloat(formData.manhours_pouring) || 0;
    const stripping = parseFloat(formData.manhours_stripping) || 0;
    const punch = parseFloat(formData.manhours_punch) || 0;
    const totalMH = forming + pouring + stripping + punch;
    const cy = parseFloat(formData.cubic_yards) || 0;
    const mhPerCY = cy > 0 ? (totalMH / cy).toFixed(2) : 0;
    
    return { totalMH, mhPerCY };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validate at least one phase has hours
    const hasPhases = parseFloat(formData.manhours_forming) > 0 ||
                      parseFloat(formData.manhours_pouring) > 0 ||
                      parseFloat(formData.manhours_stripping) > 0 ||
                      parseFloat(formData.manhours_punch) > 0;
    
    if (!hasPhases) {
      setError('Please enter manhours for at least one phase');
      setLoading(false);
      return;
    }

    try {
      await api.post('/logs', {
        project_id: parseInt(formData.project_id),
        floor_id: parseInt(formData.floor_id),
        log_date: formData.log_date,
        element_category: formData.element_category,
        element_detail: formData.element_detail,
        
        // Send phase manhours
        manhours_forming: parseFloat(formData.manhours_forming) || 0,
        manhours_pouring: parseFloat(formData.manhours_pouring) || 0,
        manhours_stripping: parseFloat(formData.manhours_stripping) || 0,
        manhours_punch: parseFloat(formData.manhours_punch) || 0,
        
        cubic_yards: parseFloat(formData.cubic_yards),
        crew_size: parseInt(formData.crew_size),
        equipment_used: formData.equipment_used,
        equipment_hours: formData.equipment_hours ? parseFloat(formData.equipment_hours) : null,
        plywood_reuse: formData.plywood_reuse,
        scaffold_tiers: formData.scaffold_tiers ? parseInt(formData.scaffold_tiers) : null,
        formwork_notes: formData.formwork_notes,
        notes: formData.notes
      });
      navigate('/logs');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create log');
      setLoading(false);
    }
  };

  const totals = calculateTotals();

  return (
    <div className="card">
      <h3>New Production Log</h3>
      <form onSubmit={handleSubmit}>
        {error && <div className="alert error">{error}</div>}
        
        <div className="form-group">
          <label>Project *</label>
          <select
            name="project_id"
            value={formData.project_id}
            onChange={handleChange}
            required
          >
            <option value="">Select Project</option>
            {projects.map(project => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Floor/Level *</label>
          <select
            name="floor_id"
            value={formData.floor_id}
            onChange={handleChange}
            required
            disabled={!formData.project_id}
          >
            <option value="">Select Floor</option>
            {floors.map(floor => (
              <option key={floor.id} value={floor.id}>
                {floor.name}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Date *</label>
          <input
            type="date"
            name="log_date"
            value={formData.log_date}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group">
          <label>Element Category *</label>
          <select
            name="element_category"
            value={formData.element_category}
            onChange={handleChange}
            required
          >
            <option value="">Select Category</option>
            {Object.keys(ELEMENT_CONFIG).map(category => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Element Detail *</label>
          <select
            name="element_detail"
            value={formData.element_detail}
            onChange={handleChange}
            required
            disabled={!formData.element_category}
          >
            <option value="">Select Detail</option>
            {formData.element_category && ELEMENT_CONFIG[formData.element_category].map(detail => (
              <option key={detail} value={detail}>
                {detail}
              </option>
            ))}
          </select>
        </div>

        {/* PHASE MANHOURS SECTION */}
        <div style={{ background: '#f8f9fa', padding: '15px', borderRadius: '5px', marginBottom: '15px' }}>
          <h4 style={{ marginBottom: '15px' }}>Manhours by Phase</h4>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
            <div className="form-group">
              <label>Forming Manhours</label>
              <input
                type="number"
                step="0.1"
                name="manhours_forming"
                value={formData.manhours_forming}
                onChange={handleChange}
                placeholder="0"
              />
            </div>

            <div className="form-group">
              <label>Pouring Manhours</label>
              <input
                type="number"
                step="0.1"
                name="manhours_pouring"
                value={formData.manhours_pouring}
                onChange={handleChange}
                placeholder="0"
              />
            </div>

            <div className="form-group">
              <label>Stripping Manhours</label>
              <input
                type="number"
                step="0.1"
                name="manhours_stripping"
                value={formData.manhours_stripping}
                onChange={handleChange}
                placeholder="0"
              />
            </div>

            <div className="form-group">
              <label>Punch Out Manhours</label>
              <input
                type="number"
                step="0.1"
                name="manhours_punch"
                value={formData.manhours_punch}
                onChange={handleChange}
                placeholder="0"
              />
            </div>
          </div>

          <div style={{ 
            marginTop: '15px', 
            padding: '10px', 
            background: '#fff', 
            borderRadius: '5px',
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '10px'
          }}>
            <div>
              <strong>Total Manhours:</strong> {totals.totalMH.toFixed(1)} MH
            </div>
            <div>
              <strong>MH/CY:</strong> {totals.mhPerCY}
            </div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
          <div className="form-group">
            <label>Cubic Yards *</label>
            <input
              type="number"
              step="0.01"
              name="cubic_yards"
              value={formData.cubic_yards}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Crew Size *</label>
            <input
              type="number"
              name="crew_size"
              value={formData.crew_size}
              onChange={handleChange}
              required
            />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
          <div className="form-group">
            <label>Equipment Used</label>
            <input
              type="text"
              name="equipment_used"
              value={formData.equipment_used}
              onChange={handleChange}
              placeholder="e.g., Crane, Pump Truck"
            />
          </div>

          <div className="form-group">
            <label>Equipment Hours</label>
            <input
              type="number"
              step="0.1"
              name="equipment_hours"
              value={formData.equipment_hours}
              onChange={handleChange}
            />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
          <div className="form-group">
            <label>Plywood Reuse</label>
            <select
              name="plywood_reuse"
              value={formData.plywood_reuse === null ? '' : formData.plywood_reuse.toString()}
              onChange={handleChange}
            >
              <option value="">Not Tracked</option>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          </div>

          <div className="form-group">
            <label>Scaffold Tiers</label>
            <input
              type="number"
              name="scaffold_tiers"
              value={formData.scaffold_tiers}
              onChange={handleChange}
            />
          </div>
        </div>

        <div className="form-group">
          <label>Formwork Notes</label>
          <textarea
            name="formwork_notes"
            value={formData.formwork_notes}
            onChange={handleChange}
            rows="3"
          />
        </div>

        <div className="form-group">
          <label>General Notes</label>
          <textarea
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            rows="3"
          />
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          <button type="submit" className="primary" disabled={loading}>
            {loading ? 'Saving...' : 'Save Log'}
          </button>
          <button type="button" onClick={() => navigate('/logs')} className="secondary">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default LogForm;